import { useEffect, useState } from "react";
import { RgbaColorPicker } from "react-colorful";
import useEyeDropper from "use-eye-dropper";
import { HEX_COLOR_FORMAT, RGBA_COLOR_FORMAT } from "../../constants.ts";
import type { ColorFormat, HexColor, RgbaColor } from "../../types.ts";
import { hexToRgba, isHexColor, rgbaToHex } from "../../utils/colors.ts";
import { notifier } from "../../utils/notifier.tsx";
import { SelectionDropdown } from "../Dropdown/SelectionDropdown.tsx";
import { ClickableIcon } from "../Icon/ClickableIcon.tsx";
import { InputNumber } from "../InputNumber/InputNumber.tsx";
import { TextField } from "../TextField/TextField.tsx";
import "./colorPicker.css";
import { ColorFormatDisplay } from "./display.ts";

export const ColorPicker = ({
  initialColor = { r: 255, g: 255, b: 255, a: 1 },
  onChange,
  defaultFormatColor = HEX_COLOR_FORMAT,
}: {
  initialColor?: RgbaColor;
  onChange?: (color: RgbaColor) => void;
  defaultFormatColor?: ColorFormat;
}) => {
  const [color, setColor] = useState<RgbaColor>(initialColor);
  const [format, setFormat] = useState<ColorFormat>(defaultFormatColor);
  const { open: openEyeDropper, isSupported: isEyeDropperSupported } =
    useEyeDropper();

  useEffect(() => {
    setColor(initialColor);
  }, [initialColor]);

  const handleChange = (newColor: RgbaColor) => {
    setColor(newColor);
    onChange?.(newColor);
  };

  const handleEyeDropperClick = async () => {
    try {
      const result = await openEyeDropper();
      // FIXME: We use the sRGBHex value to get the color in the same format as the color picker, which may not be the correct format that is used in fabric.
      // We need to add a conversion function to convert the sRGBHex value to the correct format that is used in fabric.
      const pickedColor = hexToRgba(result.sRGBHex as HexColor);
      handleChange(pickedColor);
    } catch {
      notifier.error("Failed to pick color from screen");
    }
  };

  return (
    <div className="flex-col gap-200 p-200 rounded-lg color-picker">
      <div className="color-picker__container">
        <RgbaColorPicker
          color={color}
          onChange={handleChange}
          className="flex-col self-center gap-200 !w-[240px]"
        />
        {isEyeDropperSupported() && (
          <div className="color-picker__pipette">
            <ClickableIcon
              name="Pipette"
              size="lg"
              variant="tertiary"
              onClick={handleEyeDropperClick}
              tooltip={{
                content: "Pick color from screen",
                side: "top",
              }}
            />
          </div>
        )}
      </div>
      <div className="flex-row items-center gap-100">
        <SelectionDropdown
          value={format}
          onChange={setFormat}
          options={[
            {
              value: HEX_COLOR_FORMAT,
              label: ColorFormatDisplay[HEX_COLOR_FORMAT],
            },
            {
              value: RGBA_COLOR_FORMAT,
              label: ColorFormatDisplay[RGBA_COLOR_FORMAT],
            },
          ]}
          size="sm"
          side="top"
        />
        {format === RGBA_COLOR_FORMAT ? (
          <RgbaColorInput rgbaValue={color} onRgbaValueChange={handleChange} />
        ) : (
          <HexColorInput
            hexValue={rgbaToHex(color)}
            onHexValueChange={(hexValue) => handleChange(hexToRgba(hexValue))}
          />
        )}
      </div>
    </div>
  );
};

const RgbaColorInput = ({
  rgbaValue,
  onRgbaValueChange,
}: {
  rgbaValue: RgbaColor;
  onRgbaValueChange: (value: RgbaColor) => void;
}) => (
  // XXX: The min/max property of the `InputNumber` component ensures that the input value is always a valid rgba color
  <div className="flex-row items-center gap-100">
    <InputNumber
      className="w-[36px]"
      min={0}
      max={255}
      value={Math.round(rgbaValue.r)}
      onChange={(r) => onRgbaValueChange({ ...rgbaValue, r })}
    />
    <InputNumber
      className="w-[36px]"
      min={0}
      max={255}
      value={Math.round(rgbaValue.g)}
      onChange={(g) => onRgbaValueChange({ ...rgbaValue, g })}
    />
    <InputNumber
      className="w-[36px]"
      min={0}
      max={255}
      value={Math.round(rgbaValue.b)}
      onChange={(b) => onRgbaValueChange({ ...rgbaValue, b })}
    />
    <InputNumber
      className="w-[50px]"
      min={0}
      max={100}
      value={rgbaValue.a * 100}
      onChange={(a) => onRgbaValueChange({ ...rgbaValue, a: a / 100 })}
      suffix="%"
    />
  </div>
);

const HexColorInput = ({
  hexValue,
  onHexValueChange,
}: {
  hexValue: HexColor;
  onHexValueChange: (value: HexColor) => void;
}) => {
  // XXX: We store the input value in different state to store intermediary values that are not valid hex colors
  const [inputValue, setInputValue] = useState<string>(hexValue);

  useEffect(() => {
    setInputValue(hexValue);
  }, [hexValue]);

  return (
    <TextField
      size="sm"
      type="text"
      className="w-[90px]"
      inputClassName="hide-controls border-0"
      value={inputValue.toUpperCase()}
      onChange={(text) => {
        const color = !text.startsWith("#") ? "#" + text : text;
        setInputValue(color);
        if (isHexColor(color)) onHexValueChange(color);
      }}
      placeholder="#RRGGBB"
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape" || e.key === "Enter") e.currentTarget.blur();
      }}
      onBlur={() => onHexValueChange(hexValue)}
    />
  );
};
